import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { useDropzone } from 'react-dropzone';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import { Upload, FileText, CheckCircle2, AlertTriangle, Table, ArrowLeft } from 'lucide-react';
import { contactsService } from '@/services/contacts.service';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { useCompanyCountry, useLabels } from '@/contexts/WorkspaceContext';

const TARGET_FIELDS = [
  { value: '__skip__', label: '— Skip —' },
  { value: 'firstName', label: 'First name' },
  { value: 'lastName', label: 'Last name' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'whatsappPhone', label: 'WhatsApp phone' },
  { value: 'company', label: 'Company' },
  { value: 'jobTitle', label: 'Job title' },
  { value: 'city', label: 'City' },
  { value: 'country', label: 'Country' },
  { value: 'notes', label: 'Notes' },
];

const COMMON_COUNTRY_CODES = ['PK', 'AE', 'US', 'GB', 'SA', 'IN', 'CN'];

export default function ContactImport() {
  const t = useLabels();
  const nav = useNavigate();
  const [filename, setFilename] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  // Numbers without a prefix are read as the company's country unless the user picks another one.
  const companyCountry = useCompanyCountry();
  const [pickedCountry, setDefaultCountry] = useState<string | null>(null);
  const defaultCountry = pickedCountry ?? companyCountry;
  const countryOptions = Array.from(new Set([companyCountry, ...COMMON_COUNTRY_CODES]));
  const [importUid, setImportUid] = useState<string | null>(null);
  const [status, setStatus] = useState<any>(null);

  const onDrop = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setFilename(file.name);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (result) => {
        setHeaders(result.meta.fields ?? []);
        setRows(result.data);
        const initial = Object.fromEntries(
          (result.meta.fields ?? []).map((h) => {
            const lower = h.toLowerCase();
            if (lower.includes('phone')) return [h, 'phone'];
            if (lower.includes('first')) return [h, 'firstName'];
            if (lower.includes('last')) return [h, 'lastName'];
            if (lower.includes('email')) return [h, 'email'];
            return [h, '__skip__'];
          }),
        );
        setMapping(initial);
      },
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'] },
    maxFiles: 1,
  });

  const submit = async () => {
    const r = await contactsService.importCsv({ filename, mapping, rows, defaultCountryCode: defaultCountry });
    setImportUid(r.importUid);
  };

  useEffect(() => {
    if (!importUid) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const s = await contactsService.importStatus(importUid);
      setStatus(s);
      if (s.status === 'completed' || s.status === 'failed') return;
      setTimeout(tick, 1500);
    };
    tick();
    return () => { cancelled = true; };
  }, [importUid]);

  const phonesMapped = Object.values(mapping).some(field => field === 'phone' || field === 'email');
  const pct = status ? Math.round((status.processed / Math.max(1, status.totalRows)) * 100) : 0;

  return (
    <>
      <Topbar
        title={`Import ${t('contacts').toLowerCase()}`}
        breadcrumb={`Audience / ${t('contacts')}`}
        description="Upload a CSV file to bulk-import contacts."
        back={
          <button
            onClick={() => nav('/contacts')}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary"
            aria-label="Back to contacts"
          >
            <ArrowLeft size={16} />
          </button>
        }
      />

      <div className="mx-auto w-full max-w-[800px] px-7 py-7 space-y-4">
        {/* Drop zone */}
        {!filename && (
          <div
            {...getRootProps()}
            className={clsx(
              'rounded-2xl border-2 border-dashed p-14 text-center cursor-pointer transition-all',
              isDragActive
                ? 'border-primary bg-primary-soft'
                : 'border-border bg-surface hover:border-border-strong hover:bg-surface-alt',
            )}
          >
            <input {...getInputProps()} />
            <div className="flex h-14 w-14 mx-auto mb-4 items-center justify-center rounded-2xl bg-primary-soft text-primary">
              <Upload size={24} />
            </div>
            <p className="font-semibold text-text-primary">
              {isDragActive ? 'Drop the file here' : 'Drop a CSV file here or click to upload'}
            </p>
            <p className="mt-1 text-sm text-text-muted">Up to 50,000 rows. Phone or email column is required.</p>
          </div>
        )}

        {/* File loaded — mapping */}
        {filename && !importUid && (
          <>
            <Card>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-soft text-primary">
                  <FileText size={18} />
                </div>
                <div>
                  <div className="font-semibold text-text-primary">{filename}</div>
                  <div className="text-xs text-text-muted">{rows.length.toLocaleString()} rows detected</div>
                </div>
                <button
                  onClick={() => { setFilename(''); setHeaders([]); setRows([]); setMapping({}); }}
                  className="ml-auto text-xs text-text-muted hover:text-text-primary transition-colors"
                >
                  Change file
                </button>
              </div>
            </Card>

            <Card>
              <SectionHeader title="Map columns" icon={<Table size={16} />} className="mb-4" />
              <div className="divide-y divide-border">
                {headers.map((h) => (
                  <div key={h} className="grid grid-cols-2 items-center gap-4 py-3">
                    <div>
                      <div className="text-sm font-medium text-text-primary">{h}</div>
                      <div className="text-[11px] text-text-muted">CSV column</div>
                    </div>
                    <Select
                      value={mapping[h] ?? '__skip__'}
                      onChange={(e) => setMapping((m) => ({ ...m, [h]: e.target.value }))}
                    >
                      {TARGET_FIELDS.map((f) => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                      <option value={`custom.${h}`}>Custom field: {h}</option>
                    </Select>
                  </div>
                ))}
              </div>

              <div className="mt-4 border-t border-border pt-4">
                <FormField label="Default country code (used when phone has no country prefix)">
                  <Select value={defaultCountry} onChange={(e) => setDefaultCountry(e.target.value)} className="w-40">
                    {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </FormField>
              </div>
            </Card>

            {!phonesMapped && (
              <div className="flex items-center gap-2 rounded-xl border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger">
                <AlertTriangle size={15} className="shrink-0" />
                Map a Phone or Email column before importing.
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => nav('/contacts')}>Cancel</Button>
              <Button disabled={!phonesMapped} onClick={submit}>Start import</Button>
            </div>
          </>
        )}

        {/* Import in progress / done */}
        {importUid && (
          <Card>
            <SectionHeader
              title={status?.status === 'completed' ? 'Import complete!' : 'Importing…'}
              icon={status?.status === 'completed' ? <CheckCircle2 size={16} /> : <Upload size={16} />}
              className="mb-4"
            />

            {!status ? (
              <div className="space-y-2">
                <div className="skeleton h-4 w-full rounded" />
                <div className="skeleton h-2 w-full rounded-full" />
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="mb-1.5 flex justify-between text-xs text-text-muted tabular-nums">
                    <span>{status.processed.toLocaleString()} / {status.totalRows.toLocaleString()} processed</span>
                    <span>{pct}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-surface-alt">
                    <div
                      className="h-full rounded-full bg-gold-gradient transition-all duration-500"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-xl border border-border bg-surface-alt p-3 text-center">
                    <div className="font-display text-xl font-bold text-success tabular-nums">{status.inserted.toLocaleString()}</div>
                    <div className="text-[10px] text-text-muted uppercase tracking-wide mt-0.5">Inserted</div>
                  </div>
                  <div className="rounded-xl border border-border bg-surface-alt p-3 text-center">
                    <div className="font-display text-xl font-bold text-text-muted tabular-nums">{status.skippedDuplicates?.toLocaleString() ?? '—'}</div>
                    <div className="text-[10px] text-text-muted uppercase tracking-wide mt-0.5">Skipped dupes</div>
                  </div>
                  <div className="rounded-xl border border-border bg-surface-alt p-3 text-center">
                    <div className={clsx('font-display text-xl font-bold tabular-nums', status.errors > 0 ? 'text-danger' : 'text-text-muted')}>
                      {status.errors?.toLocaleString() ?? '—'}
                    </div>
                    <div className="text-[10px] text-text-muted uppercase tracking-wide mt-0.5">Errors</div>
                  </div>
                </div>

                {status.status === 'completed' && (
                  <div className="flex justify-end">
                    <Button onClick={() => { toast.success('Import complete'); nav('/contacts'); }}>
                      View contacts
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}
      </div>
    </>
  );
}
